import { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage, fetchStakes } from '../../utils/api';
import { formatDate } from '../../utils/formatDate';
import './TransactionsList.css';

const TransactionsList = ({ refreshKey }) => {
  const [transactions, setTransactions] = useState([]);
  const [searchWalletAddress, setSearchWalletAddress] = useState('');
  const [syncedBlock, setSyncedBlock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // 'all' | 'admin' | 'self' — кто отправлял транзакцию стейкинга.
  const [stakedByFilter, setStakedByFilter] = useState('all');

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // Реестр на бэкенде стал проекцией блокчейна и хранит в том числе
      // выплаченные стейкинги — раньше запись физически удалялась при выплате.
      // Панель показывает только активные, поэтому фильтр обязателен.
      const { stakes, syncedBlock: block } = await fetchStakes({
        claimed: false,
      });

      setTransactions(stakes);
      setSyncedBlock(block);
    } catch (fetchError) {
      console.error('Ошибка отображения стейков:', fetchError);
      // Пустая таблица при сбое неотличима от пустого реестра, а разница для
      // оператора принципиальная: во втором случае искать нечего, в первом —
      // данные есть, но панель их не видит.
      setError(apiErrorMessage(fetchError));
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // refreshKey меняется после успешного стейкинга: список перечитывается без
  // перезагрузки страницы, поэтому подключение кошелька не сбрасывается.
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions, refreshKey]);

  const handleSearchChange = (event) =>
    setSearchWalletAddress(event.target.value);

  const filteredTransactions = transactions.filter((tx) => {
    const search = searchWalletAddress.toLowerCase();

    // Поиск идёт и по получателю, и по отправителю: иначе нельзя найти всё,
    // что оформил конкретный админ.
    const matchesSearch =
      tx.receiver.toLowerCase().includes(search) ||
      (tx.sender ?? '').toLowerCase().includes(search);

    const matchesStakedBy =
      stakedByFilter === 'all' || tx.stakedBy === stakedByFilter;

    return matchesSearch && matchesStakedBy;
  });

  const sortedTransactions = filteredTransactions.sort(
    (a, b) => new Date(a.unlock_time) - new Date(b.unlock_time),
  );

  const shortAddress = (address) =>
    address ? `${address.slice(0, 6)}…${address.slice(-4)}` : '—';

  // Пустая таблица обязана объяснять, почему она пустая: «ещё грузится»,
  // «реестр пуст» и «фильтр ничего не нашёл» — три разных вывода для оператора.
  const emptyMessage = () => {
    if (loading) return 'Загрузка реестра...';
    if (transactions.length === 0) return 'В реестре нет активных стейкингов';
    return 'По заданному поиску и фильтру ничего не найдено';
  };

  return (
    <div className="transactions_container">
      <div className="search_container">
        <input
          type="text"
          value={searchWalletAddress}
          onChange={handleSearchChange}
          placeholder="Поиск по адресу получателя или отправителя"
        />
        <select
          value={stakedByFilter}
          onChange={(event) => setStakedByFilter(event.target.value)}
          className="staked_by_filter"
        >
          <option value="all">Все стейкинги</option>
          <option value="admin">Оформил админ</option>
          <option value="self">Оформил пользователь</option>
        </select>
        {syncedBlock && (
          <span
            className="synced_block"
            title="До какого блока данные актуальны"
          >
            данные на блок {syncedBlock}
          </span>
        )}
      </div>
      <table>
        <thead>
          <tr>
            <th>№</th>
            <th>Адрес кошелька</th>
            <th>Кол-во токенов</th>
            <th>Период</th>
            <th>Ставка</th>
            <th>Оформил</th>
            <th>Вывод из стейкинга</th>
            <th className="button_column">Транзакция</th>
          </tr>
        </thead>
        <tbody>
          {sortedTransactions.length === 0 && (
            <tr className="table_message_row">
              <td colSpan={8} className="table_message">
                {error ? (
                  <>
                    <span className="table_message__error">
                      Не удалось загрузить реестр: {error}
                    </span>
                    <button
                      type="button"
                      className="retry_button"
                      onClick={fetchTransactions}
                      disabled={loading}
                    >
                      Повторить
                    </button>
                  </>
                ) : (
                  emptyMessage()
                )}
              </td>
            </tr>
          )}

          {sortedTransactions.map((tx, index) => (
            <tr key={tx.hash || index}>
              <td>{index + 1}</td>
              <td>
                <a
                  href={`https://bscscan.com/address/${tx.receiver}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {tx.receiver}
                </a>
              </td>
              <td>{Number(tx.amount).toLocaleString('ru-RU')}</td>
              <td>{tx.staking_period} дней</td>
              <td>{tx.rate}%</td>
              <td>
                {/* Отправитель транзакции: сам пользователь при stake(),
                    админ при stakeFor(). Это разные адреса, и различие видно
                    только по отправителю, а не по получателю. */}
                {tx.stakedBy === 'self' ? (
                  <span className="staked_by_self">пользователь</span>
                ) : (
                  <a
                    href={`https://bscscan.com/address/${tx.sender}`}
                    target="_blank"
                    rel="noreferrer"
                    title={tx.sender}
                    className="staked_by_admin"
                  >
                    {shortAddress(tx.sender)}
                  </a>
                )}
              </td>
              <td>{formatDate(tx.unlock_time)}</td>
              <td>
                <a
                  href={`https://bscscan.com/tx/${tx.hash}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn"
                >
                  Ссылка на транзакцию
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TransactionsList;
